/**
 * Component: BirdEye chat
 * Injects one BirdEye webchat and adapts the vendor's undocumented window.be_webchat into
 * window.HudsonBirdEye.
 *
 * Injected here rather than printed by Liquid: only one widget can run per page (the bundle
 * keeps its globals unnamespaced and claims `window.onmessage` by assignment, so a second one
 * silently breaks the first), and the active location arrives as ?loc=<id>, which Liquid
 * cannot read.
 *
 * Switching location always reloads — the vendor has no destroy API and its globals cannot be
 * unwound, so only a fresh page load can swap widgets.
 *
 * Every trigger in the theme goes through the delegation at the bottom of this file, including
 * the PDP Text action, which opts in with data-birdeye-chat-open. Nothing else should
 * re-implement the open-on-click contract.
 */
(() => {
  const LABEL = "[live chat]";
  const WIDGET_KIND = "chat";
  const ID_SEGMENT_FROM_END = 2;

  const CONFIG_SELECTOR = "[data-birdeye-chat-config]";
  const TRIGGER_SELECTOR = "[data-birdeye-chat-open], a[href], js-link[href]";
  const DRAWER_TOGGLE_SELECTOR = "[aria-controls='wt-drawer-nav']";
  const DRAWER_CLOSE_SELECTOR = "#wt-drawer-nav .wt-drawer__close";
  const POLL_INTERVAL_MS = 250;
  const READY_TIMEOUT_MS = 20000;

  const LAUNCHER_ID = "bewebchat";
  const WINDOW_ID = "window-iframe";
  const VENDOR_OPEN_TYPE = "post-message-from-outside";
  const VENDOR_OPENED_PREFIX = "icon-clicked";
  const VENDOR_CLOSED_MESSAGE = "icon-closed***floating";
  const LAUNCHER_RESET_TIMEOUT_MS = 1000;
  const HOLD_CHECK_MS = 50;
  const CLOSED_STORAGE_KEY = "hudson.birdeye.chat.closedAt";
  // The vendor re-opens a chat that was left open less than 30 minutes ago.
  const CLOSED_TTL_MS = 30 * 60 * 1000;
  const CLOSE_BUTTON_CLASS = "birdeye-chat-close";
  const CLOSE_VISIBLE_CLASS = "birdeye-chat-close--visible";
  const HELD_CLASS = "birdeye-chat-held";
  const DRAWER_OPEN_CLASS = "birdeye-chat-drawer-open";
  // BirdEye pins a full-height drawer to the screen edge and draws its own X there.
  // The floating card (inset 20px) has no header X. The drawer choice is fixed when
  // the window opens, so a later resize must follow the window, not the viewport.
  const DRAWER_EDGE = "0px";
  const DRAWER_MAX_WIDTH_PX = 960;
  const CLOSE_INSET_PX = 16;
  const CLOSE_ICON =
    '<svg class="birdeye-chat-close__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  const embed = window.BirdeyeEmbed;
  if (!embed) {
    console.error(`${LABEL} birdeye-embed.js did not load, chat disabled.`);
    return;
  }

  const CONTAINER_SELECTOR = `[id^="bf-revz-widget-"]:not([${embed.WIDGET_ATTRIBUTE}]), [id^="bf-revz-widget-"][${embed.WIDGET_ATTRIBUTE}="${WIDGET_KIND}"]`;

  const config = embed.readJsonConfig(document.querySelector(CONFIG_SELECTOR), LABEL);
  if (!config) return;

  const triggerToken = config.token || "live-chat";
  const locations = Array.isArray(config.locations) ? config.locations : [];

  /* ---------------------------------------------------------------- location */
  const sameId = (a, b) => String(a) === String(b);

  function findLocation(id) {
    if (id === null || id === "") return null;
    return locations.find((entry) => sameId(entry.id, id)) || null;
  }

  // Only the showroom chat links (store locator, store page) pick a location, via ?loc=.
  // The header location is deliberately ignored: everywhere else gets the general chat,
  // or the first showroom while the general code is empty.
  function resolveLocation() {
    const requestedId = new URLSearchParams(window.location.search).get("loc");

    if (requestedId) {
      const requested = findLocation(requestedId);
      if (requested) return requested;
      console.warn(
        `[live chat] no location with Location ID "${requestedId}", using the default chat.`,
      );
    }

    if (!config.default && locations.length) return locations[0];

    return null;
  }

  const activeLocation = resolveLocation();

  /* ------------------------------------------------------------------- code */
  const chosen =
    activeLocation && activeLocation.code
      ? { code: activeLocation.code, source: `the code for location ${activeLocation.id}` }
      : { code: config.default, source: "the general chat code" };

  if (!chosen.code) {
    console.warn(
      "[live chat] no chat to show on this page: Theme settings → Live Chat → Chat widget code is empty, and this page has no location. Fill the general code, or open the page with ?loc=<Location ID>.",
    );
    return;
  }

  const parsed = embed.parseEmbedCode(chosen.code, {
    label: LABEL,
    source: chosen.source,
    idSegmentFromEnd: ID_SEGMENT_FROM_END,
  });
  if (!parsed) return;

  /* ---------------------------------------------------------------- injection */
  function injectWidget() {
    if (document.querySelector(CONTAINER_SELECTOR)) {
      console.error(
        "[live chat] a chat widget is already on the page, so this one was not added. Remove the duplicate — two widgets break each other.",
      );
      return false;
    }

    embed.injectWidget(document.body, parsed, WIDGET_KIND);
    return true;
  }

  if (!injectWidget()) return;

  /* ------------------------------------------------------------------ adapter */
  const isVendorReady = () => typeof window.be_webchat?.open === "function";

  function openVendorChat() {
    try {
      if (!isVendorReady()) return false;
      // A held page may already be re-closing the vendor's automatic reopen.
      // Drop that close, or its reply posts icon-closed over the open just requested.
      cancelPendingClose();
      releaseHold();
      rememberClosed(false);
      window.be_webchat.open();
      return true;
    } catch (error) {
      return false;
    }
  }

  function resolve() {
    if (!isVendorReady()) return null;

    renderCloseButton();

    if (!window.HudsonBirdEye) {
      window.HudsonBirdEye = {
        open: openVendorChat,
        isReady: isVendorReady,
        token: triggerToken,
      };
    }

    return window.HudsonBirdEye;
  }

  window.HudsonChat = {
    locations,
    getLocation: () => activeLocation,
    setLocation(id) {
      const next = findLocation(id);
      if (!next) {
        console.warn(`[live chat] no location with Location ID "${id}", nothing changed.`);
        return false;
      }
      if (activeLocation && sameId(activeLocation.id, next.id)) return true;

      const url = new URL(window.location.href);
      url.searchParams.set("loc", String(next.id));
      window.location.assign(url.toString());
      return true;
    },
  };

  /* ------------------------------------------------------------ close button */
  // A second X for the floating chat, whose only close control is the launcher in a
  // cross-origin iframe. Closing replays the launcher's own close: the launcher is sent the
  // vendor "open" command saying the chat is closed, which makes it click its own toggle
  // back to the chat icon, and the page handler gets the "icon-closed" message the launcher
  // would send. The vendor does not remember a close done this way and re-opens the chat on
  // the next page, so that is remembered here and the re-open is undone while hidden.
  let isHeld = wasClosedRecently();
  if (isHeld) document.body.classList.add(HELD_CLASS);

  function rememberClosed(closed) {
    try {
      if (closed) window.localStorage.setItem(CLOSED_STORAGE_KEY, String(Date.now()));
      else window.localStorage.removeItem(CLOSED_STORAGE_KEY);
    } catch (error) {
      // Storage can be blocked; the chat then simply re-opens like the vendor's own.
    }
  }

  function wasClosedRecently() {
    try {
      const closedAt = Number(window.localStorage.getItem(CLOSED_STORAGE_KEY));
      if (!(closedAt > 0) || closedAt > Date.now()) return false;
      return Date.now() - closedAt < CLOSED_TTL_MS;
    } catch (error) {
      return false;
    }
  }

  function releaseHold() {
    isHeld = false;
    document.body.classList.remove(HELD_CLASS);
  }

  let finishClosing = null;
  let closeFallbackTimer = null;
  // Set when the close gives up waiting. The launcher's late "icon-clicked" would
  // otherwise look like a customer open and erase the stored close.
  let ignoreLateOpen = false;

  function cancelPendingClose() {
    window.clearTimeout(closeFallbackTimer);
    closeFallbackTimer = null;
    finishClosing = null;
    ignoreLateOpen = false;
  }

  // The launcher answers the command with "icon-clicked"; the window is hidden only after
  // that, so the answer cannot re-open it.
  function closeVendorChat(onClosed) {
    const launcher = document.getElementById(LAUNCHER_ID);
    if (!launcher?.contentWindow || finishClosing) return;

    closeFallbackTimer = window.setTimeout(() => {
      ignoreLateOpen = true;
      finishClosing?.();
    }, LAUNCHER_RESET_TIMEOUT_MS);
    finishClosing = () => {
      window.clearTimeout(closeFallbackTimer);
      closeFallbackTimer = null;
      finishClosing = null;
      window.postMessage(VENDOR_CLOSED_MESSAGE, "*");
      onClosed?.();
    };

    launcher.contentWindow.postMessage(
      {
        type: VENDOR_OPEN_TYPE,
        beWebchatWindowObj: JSON.stringify({ ...window.be_webchat, windowOpened: false }),
      },
      "*",
    );
  }

  function handleCloseClick() {
    rememberClosed(true);
    closeVendorChat(() => document.getElementById(LAUNCHER_ID)?.focus());
  }

  function vendorHasOwnClose(chatWindow) {
    return chatWindow.style.right === DRAWER_EDGE || chatWindow.style.left === DRAWER_EDGE;
  }

  function placeCloseButton(button, chatWindow) {
    if (window.innerWidth >= DRAWER_MAX_WIDTH_PX) {
      button.style.top = "";
      button.style.right = "";
      return;
    }

    const box = chatWindow.getBoundingClientRect();
    const top = Math.max(box.top, 0) + CLOSE_INSET_PX;
    button.style.top = `${Math.round(top)}px`;
    button.style.right = `${Math.round(window.innerWidth - box.right + CLOSE_INSET_PX)}px`;
  }

  function syncCloseButton() {
    const chatWindow = document.getElementById(WINDOW_ID);
    const button = document.querySelector(`.${CLOSE_BUTTON_CLASS}`);
    if (!chatWindow || !button) return;

    const open = chatWindow.getBoundingClientRect().height > 1 && chatWindow.style.height !== "0px";
    const isDrawer = vendorHasOwnClose(chatWindow);
    const show = open && !isDrawer;
    document.body.classList.toggle(DRAWER_OPEN_CLASS, open && isDrawer);
    button.classList.toggle(CLOSE_VISIBLE_CLASS, show);
    if (show) placeCloseButton(button, chatWindow);
  }

  let syncFrame = 0;

  function scheduleSync() {
    if (syncFrame) return;
    syncFrame = window.requestAnimationFrame(() => {
      syncFrame = 0;
      syncCloseButton();
    });
  }

  // The hold class hides the window. Drop it only once the vendor has actually closed,
  // or the reopened chat flashes on every page within the 30 minutes.
  function releaseHoldWhenClosed() {
    const startedAt = Date.now();
    const tick = () => {
      const chatWindow = document.getElementById(WINDOW_ID);
      const closed =
        !chatWindow ||
        chatWindow.style.height === "0px" ||
        chatWindow.getBoundingClientRect().height <= 1;
      if (closed || Date.now() - startedAt >= LAUNCHER_RESET_TIMEOUT_MS) {
        releaseHold();
        return;
      }
      window.setTimeout(tick, HOLD_CHECK_MS);
    };
    tick();
  }

  let chatWindowWatched = false;

  function watchChatWindow(chatWindow) {
    if (chatWindowWatched) return;
    chatWindowWatched = true;
    new MutationObserver(scheduleSync).observe(chatWindow, {
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  function renderCloseButton() {
    const chatWindow = document.getElementById(WINDOW_ID);
    if (!chatWindow || document.querySelector(`.${CLOSE_BUTTON_CLASS}`)) return;

    const button = document.createElement("button");
    button.type = "button";
    button.className = CLOSE_BUTTON_CLASS;
    button.setAttribute("aria-label", config.closeLabel || "Close chat");
    button.innerHTML = CLOSE_ICON;
    button.addEventListener("click", handleCloseClick);
    chatWindow.after(button);

    watchChatWindow(chatWindow);
    syncCloseButton();
  }

  function handleVendorMessage(event) {
    const launcher = document.getElementById(LAUNCHER_ID);
    if (!launcher || event.source !== launcher.contentWindow) return;
    if (typeof event.data !== "string" || !event.data.startsWith(VENDOR_OPENED_PREFIX)) return;

    renderCloseButton();
    syncCloseButton();
    if (finishClosing) {
      finishClosing();
      return;
    }

    if (ignoreLateOpen) {
      ignoreLateOpen = false;
      return;
    }

    const openedByCustomer = document.activeElement === launcher;
    if (isHeld && !openedByCustomer) {
      closeVendorChat(() => {
        rememberClosed(true);
        releaseHoldWhenClosed();
      });
      return;
    }

    releaseHold();
    rememberClosed(false);
  }

  /* -------------------------------------------------------------- deep link */
  let autoOpenPending = window.location.hash.slice(1) === triggerToken;

  function consumeAutoOpen(widget) {
    if (!autoOpenPending) return;

    autoOpenPending = false;
    widget.open();
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }

  function handleHashChange() {
    if (window.location.hash.slice(1) !== triggerToken) return;

    autoOpenPending = true;

    const widget = resolve();
    if (widget) consumeAutoOpen(widget);
  }

  const pollTimer = window.setInterval(() => {
    const widget = resolve();
    if (!widget) return;

    window.clearInterval(pollTimer);
    consumeAutoOpen(widget);
  }, POLL_INTERVAL_MS);
  window.setTimeout(() => window.clearInterval(pollTimer), READY_TIMEOUT_MS);

  /* -------------------------------------------------------------- triggers */
  function isChatTrigger(element) {
    if (element.hasAttribute("data-birdeye-chat-open")) return true;

    const href = element.getAttribute("href");
    if (!href) return false;

    const hashIndex = href.indexOf("#");
    if (hashIndex === -1) return false;

    return href.slice(hashIndex + 1) === triggerToken;
  }

  function changesLocation(element) {
    const href = element.getAttribute("href");
    if (!href) return false;

    const requestedId = href.match(/[?&]loc=([^&#]+)/);
    if (!requestedId) return false;

    return !activeLocation || !sameId(activeLocation.id, decodeURIComponent(requestedId[1]));
  }

  function markAsDialogTrigger(event) {
    if (!resolve()) return;

    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger || !isChatTrigger(trigger) || changesLocation(trigger)) return;

    trigger.setAttribute("aria-haspopup", "dialog");
  }

  function closeNavDrawer() {
    if (!document.querySelector(`${DRAWER_TOGGLE_SELECTOR}[aria-expanded="true"]`)) return;

    document.querySelector(DRAWER_CLOSE_SELECTOR)?.click();
  }

  function handleDocumentClick(event) {
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger || !isChatTrigger(trigger)) return;

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      trigger.getAttribute("target") === "_blank" ||
      changesLocation(trigger)
    ) {
      return;
    }

    const widget = resolve();
    if (!widget) return;

    event.preventDefault();
    event.stopPropagation();
    trigger.setAttribute("aria-haspopup", "dialog");
    closeNavDrawer();
    widget.open();
  }

  const listeners = new AbortController();
  const { signal } = listeners;

  document.addEventListener("click", handleDocumentClick, { capture: true, signal });
  document.addEventListener("focusin", markAsDialogTrigger, { signal });
  document.addEventListener("pointerover", markAsDialogTrigger, { signal });
  window.addEventListener("hashchange", handleHashChange, { signal });
  window.addEventListener("message", handleVendorMessage, { signal });
  window.addEventListener("resize", scheduleSync, { signal });
})();
